import { useContext, useEffect, useMemo, useState } from 'react';
import { useScrollDirection } from 'react-use-scroll-direction';
import { Col, Row, Timeline } from 'antd';
import { useParams } from 'react-router-dom';
import ButtonIcon from 'components/buttonIcon';
import Icon from 'components/icons';
import { Spinner } from 'components/loadings';
import ModalDrawer from 'components/microLayouts/drawer';
import { ActivityLogContext } from 'contexts/activityLog';
import { SessionContext } from 'contexts/session';
import {
  ActivityLogContainerMobile,
  ActivityLogDrawerContainer,
  ActivityLogMobileTitle,
  NoMoreActivitiesContainer,
  NoMoreActivitiesIconContainer,
} from '../styles';
import ActivityLogFiltersModal, { IModalActivityLogFiltersProps } from './ActivityLogFiltersModal';
import ActivityLogItemMobile from './ActivityLogItemMobile';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface IActivityLogDrawer {
  activityLogDrawerVisible: boolean;
}

const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
  );
};

const ActivityLogDrawer = ({ activityLogDrawerVisible }: IActivityLogDrawer): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const {
    fetching,
    actions,
    table,
    filters: { page },
    isEmpty,
  } = useContext(ActivityLogContext);
  const { isStaff } = useContext(SessionContext);
  const { dashboardAccountPlaylistAssignment } = useFlags();
  const showAccountPlaylistActions = Boolean(dashboardAccountPlaylistAssignment);
  const { isScrollingDown, scrollTargetRef } = useScrollDirection();
  const [showFiltersModal, setShowFiltersModal] = useState<boolean>(false);

  // When the LD flag is OFF, hide account-playlist activity entries so the feature is invisible.
  // Client-side filtering is a safe guard in case server-side filtering isn't available.
  const visibleItems = useMemo(() => {
    const items = table?.items ?? [];
    if (showAccountPlaylistActions) return items;
    return items.filter((item) => !item.action?.startsWith('account.playlist.'));
  }, [showAccountPlaylistActions, table?.items]);

  useEffect(() => {
    if (showAccountPlaylistActions) return;
    if (fetching) return;
    if (!actions?.onChangeFilter) return;

    const loadedCount = table?.items?.length ?? 0;
    const totalCount = table?.count ?? 0;
    if (!loadedCount) return;
    if (visibleItems.length > 0) return;
    if (loadedCount >= totalCount) return;

    // If all fetched items are filtered out (flag OFF), request the next page to avoid an empty UI
    // when lazy loading is based on scroll and there is nothing to scroll.
    actions.onChangeFilter({ name: 'page', value: (parseInt(page) + 1).toString() });
  }, [
    actions,
    fetching,
    page,
    showAccountPlaylistActions,
    table?.count,
    table?.items?.length,
    visibleItems.length,
  ]);

  const handleScroll = (e) => {
    const lastChild = e.target.firstChild?.lastChild;
    if (!lastChild) return;
    if (
      isScrollingDown &&
      !fetching &&
      table.items.length !== table.count &&
      isInViewport(lastChild)
    ) {
      actions.onChangeFilter({ name: 'page', value: (parseInt(page) + 1).toString() });
    }
  };

  const handleClearFilters = () => {
    isStaff ? actions.onResetActivities(id) : actions.onResetActivities();
    setShowFiltersModal(false);
  };

  const handleApplyFilters = (modalFilters: IModalActivityLogFiltersProps) => {
    const newFilters = [
      { name: 'page', value: 1 },
      { name: 'append_results', value: false },
    ];
    Object.keys(modalFilters).map((key) => {
      newFilters.push({ name: key, value: modalFilters[key] });
    });
    actions.onChangeFilters(newFilters);
    setShowFiltersModal(false);
  };

  return (
    <>
      <ModalDrawer
        closable={false}
        placement="bottom"
        visible={activityLogDrawerVisible}
        height="92%"
        destroyOnClose
      >
        <ActivityLogDrawerContainer>
          <Spinner active={fetching} />
          <Row>
            <Col xs={20} style={{ margin: '10px 0px 0px 0px' }}>
              <ActivityLogMobileTitle>Account recent activity</ActivityLogMobileTitle>
            </Col>
            <Col xs={4}>
              <ButtonIcon
                $primary
                icon={'Filter'}
                onClick={() => setShowFiltersModal(true)}
                style={{ float: 'right' }}
              />
            </Col>
          </Row>
          <ActivityLogContainerMobile
            fetching={fetching}
            className="mt-5"
            onScroll={handleScroll}
            ref={scrollTargetRef}
          >
            {!isEmpty && visibleItems.length > 0 && (
              <Timeline>
                {visibleItems.map((item, index) => (
                  <ActivityLogItemMobile key={index} activity={item} />
                ))}
                {visibleItems.length > 0 && table?.items?.length === table?.count && (
                  <NoMoreActivitiesContainer>
                    <NoMoreActivitiesIconContainer>
                      <Icon icon="Check3" />
                    </NoMoreActivitiesIconContainer>
                    <span>You have no more activity.</span>
                  </NoMoreActivitiesContainer>
                )}
              </Timeline>
            )}
          </ActivityLogContainerMobile>
        </ActivityLogDrawerContainer>
      </ModalDrawer>
      {showFiltersModal && (
        <ActivityLogFiltersModal
          isModalOpen={showFiltersModal}
          onClose={() => setShowFiltersModal(false)}
          onClearFilters={handleClearFilters}
          onApplyFilters={handleApplyFilters}
        />
      )}
    </>
  );
};

export default ActivityLogDrawer;
